// ============================================================================
// Trolley payout UI — shared by the Creator Payments dialog and the hire detail
// page's payout panel, so both send and track a payout identically.
//
//   TrolleyPayoutMethodToggle — "Send via Trolley" vs "Record manual payment"
//   TrolleyPayoutSendForm     — recipient check, amount, confirm, send
//   TrolleyPayoutStatusCard   — the latest attempt: state, ids, Sync / Cancel
//
// Every rule is enforced server-side (see lib/trolley-payouts.ts). What the form
// adds is the thing only a person can do: look at WHO is about to be paid and
// confirm it, before money leaves.
// ============================================================================

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Send, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { usd } from '@/lib/refunds'
import {
  cancelTrolleyPayout, newTrolleyIdempotencyKey, previewTrolleyPayout, sendTrolleyPayout, syncTrolleyPayout,
  trolleyStatusLabel, TrolleyPayoutError, type PayoutTone, type TrolleyPayout,
} from '@/lib/trolley-payouts'

export type PayMethod = 'trolley' | 'manual'

const TONE: Record<PayoutTone, string> = {
  blue:    'border-blue-200 bg-blue-50 text-blue-900',
  amber:   'border-amber-200 bg-amber-50 text-amber-900',
  emerald: 'border-green-200 bg-green-50 text-green-900',
  red:     'border-red-200 bg-red-50 text-red-900',
  slate:   'border-slate-200 bg-slate-50 text-slate-800',
}

export function TrolleyPayoutMethodToggle({ value, onChange, disabled }: {
  value: PayMethod; onChange: (m: PayMethod) => void; disabled?: boolean
}) {
  const opt = (m: PayMethod, label: string, sub: string) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(m)}
      aria-pressed={value === m}
      className={`flex-1 rounded-md border px-3 py-2 text-left transition-colors disabled:opacity-50 ${
        value === m ? 'border-foreground bg-muted' : 'border-border hover:bg-muted/50'
      }`}
    >
      <span className="block text-sm font-medium">{label}</span>
      <span className="block text-[11px] text-muted-foreground">{sub}</span>
    </button>
  )
  return (
    <div className="flex gap-2" role="group" aria-label="Payment method">
      {opt('trolley', 'Send via Trolley', 'Pay from the BMC Trolley balance now')}
      {opt('manual', 'Record manual payment', 'Already paid in Trolley — enter the ID')}
    </div>
  )
}

// ── Send ────────────────────────────────────────────────────────────────────

export function TrolleyPayoutSendForm({ hireId, onSent, onCancel, compact }: {
  hireId: string
  /** Called after Trolley accepted (or at least received) the payout. */
  onSent: (payout: TrolleyPayout | null) => void
  onCancel?: () => void
  compact?: boolean
}) {
  // Always fetched fresh: the recipient's Trolley state is live data and the
  // whole point of this screen is to show it as it is right now.
  const preview = useQuery({
    queryKey: ['trolley-payout-preview', hireId],
    queryFn: () => previewTrolleyPayout(hireId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })

  const [amount, setAmount] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Fixed for the life of this form, so a retry after a timeout can never
  // create a second payment.
  const idempotencyKey = useMemo(() => newTrolleyIdempotencyKey(hireId), [hireId])

  if (preview.isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking the creator's Trolley account…
      </div>
    )
  }
  if (preview.isError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-600">{(preview.error as Error).message}</p>
        <Button size="sm" variant="outline" onClick={() => preview.refetch()}>Try again</Button>
      </div>
    )
  }

  const p = preview.data!
  const earned = p.decision.earnedCents
  const amountStr = amount ?? String(earned / 100)
  const amountCents = Math.round(Number(amountStr) * 100)
  const amountValid = Number.isFinite(amountCents) && amountCents > 0 && amountCents <= earned

  const blocker =
    !p.can_send ? 'You don\'t have permission to send payouts (payouts.send). Ask a super admin, or record a manual payment instead.'
    : p.live_payout ? 'A Trolley payout already exists for this hire — see its status below.'
    : !p.decision.eligible ? p.decision.blockedReason
    : p.recipient_problem

  async function send() {
    if (!p.recipient) return
    setSending(true)
    setError(null)
    try {
      const res = await sendTrolleyPayout({
        hireId, amountCents, recipientId: p.recipient.id, idempotencyKey, notes,
      })
      if (res.needs_reconciliation) toast.warning(res.message, { duration: 12_000 })
      else toast.success(res.message)
      onSent(res.payout)
    } catch (e) {
      const err = e as TrolleyPayoutError
      setError(err.message)
      // The attempt exists (failed / needs sync) — let the parent show it.
      if (err.payout) onSent(err.payout)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {p.recipient && (
        <div className="rounded-md border px-3 py-2.5 text-sm space-y-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Paying Trolley recipient</p>
              <p className="font-medium break-words">{p.recipient.name ?? '—'}</p>
              <p className="text-xs text-muted-foreground break-all">{p.recipient.email ?? p.recipient.id}</p>
            </div>
            {p.recipient.blocker
              ? <XCircle className="h-4 w-4 shrink-0 text-red-500" aria-label="Not ready" />
              : <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" aria-label="Ready" />}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Status: {p.recipient.status ?? '—'}
            {p.recipient.payoutMethod && <> · Method: {p.recipient.payoutMethod}</>}
            {p.recipient.complianceStatus && <> · Tax: {p.recipient.complianceStatus}</>}
          </p>
        </div>
      )}

      {!blocker && p.draft_batch && (
        <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            This hire is also in this week&apos;s <Link to="/creator-payments/batches" className="underline font-medium">draft payout batch</Link> ({usd(p.draft_batch.amount_cents)}).
            Sending it here pays it now, and the batch will skip it when it&apos;s sent.
          </span>
        </div>
      )}

      {blocker ? (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{blocker}</span>
        </div>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`tp-amount-${hireId}`}>Amount (USD) <span className="text-destructive">*</span></Label>
            <Input
              id={`tp-amount-${hireId}`}
              type="number" min="0.01" step="0.01" max={earned / 100}
              value={amountStr}
              onChange={e => { setAmount(e.target.value); setConfirmed(false) }}
              className={compact ? 'h-8' : undefined}
            />
            {/* Capped at earned, server-side too. The manual path allows more
                (bonuses, adjustments); sending more than earned from here is
                refused rather than warned about, because it's irreversible. */}
            <p className={`text-[11px] ${amountCents > earned ? 'text-red-600' : 'text-muted-foreground'}`}>
              {amountCents > earned
                ? `Can't send more than the ${usd(earned)} earned. Pay anything extra manually.`
                : `Earned: ${usd(earned)}${p.decision.mode === 'partial' ? ` for ${p.decision.deliveredVideos} of ${p.decision.requiredVideos} videos` : ''}`}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`tp-notes-${hireId}`}>Notes (internal)</Label>
            <Textarea
              id={`tp-notes-${hireId}`}
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional — saved on the hire when the payment lands"
            />
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={confirmed}
              onCheckedChange={v => setConfirmed(v === true)}
              disabled={!amountValid}
              className="mt-0.5"
            />
            <span>
              Send <strong>{amountValid ? usd(amountCents) : '—'}</strong> to <strong>{p.recipient?.name ?? 'this creator'}</strong> from
              the BMC Trolley balance. This can't be undone from here.
            </span>
          </label>
        </>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && (
          <Button size={compact ? 'sm' : 'default'} variant="outline" onClick={onCancel} disabled={sending}>Cancel</Button>
        )}
        {!blocker && (
          <Button size={compact ? 'sm' : 'default'} onClick={send} disabled={!confirmed || !amountValid || sending}>
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? 'Sending…' : `Send ${amountValid ? usd(amountCents) : ''} via Trolley`}
          </Button>
        )}
      </div>
    </div>
  )
}

// ── Status ──────────────────────────────────────────────────────────────────

export function TrolleyPayoutStatusCard({ payout, onChanged }: {
  payout: TrolleyPayout
  /** Refetch the hire after Sync / Cancel changed something. */
  onChanged: () => void
}) {
  const [busy, setBusy] = useState<'sync' | 'cancel' | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [cancellable, setCancellable] = useState(false)
  const s = trolleyStatusLabel(payout)
  const syncable = ['reserved', 'submitted', 'needs_reconciliation', 'processed'].includes(payout.status)

  async function sync() {
    setBusy('sync')
    try {
      const r = await syncTrolleyPayout(payout.id)
      setNote(r.note)
      setCancellable(r.cancellable)
      if (r.payout.status !== payout.status) toast.success(`Trolley payout is now: ${trolleyStatusLabel(r.payout).label}`)
      onChanged()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function cancel() {
    if (!window.confirm('Cancel this Trolley payout? Trolley has confirmed no money moved. The hire becomes payable again.')) return
    setBusy('cancel')
    try {
      await cancelTrolleyPayout(payout.id)
      toast.success('Trolley payout cancelled — no money was sent.')
      setCancellable(false)
      onChanged()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const when = payout.processed_at ?? payout.returned_at ?? payout.failed_at ?? payout.cancelled_at ?? payout.submitted_at ?? payout.created_at

  return (
    <div className={`rounded-md border px-3 py-2.5 space-y-1.5 ${TONE[s.tone]}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">
          Trolley · {s.label} · {usd(payout.amount_cents)}
        </p>
        <div className="flex gap-1.5">
          {syncable && (
            <Button size="sm" variant="outline" className="h-7 bg-white/60" onClick={sync} disabled={busy !== null}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy === 'sync' ? 'animate-spin' : ''}`} /> Sync
            </Button>
          )}
          {cancellable && !payout.payout_batch_id && (
            <Button size="sm" variant="outline" className="h-7 bg-white/60 border-red-300 text-red-700" onClick={cancel} disabled={busy !== null}>
              Cancel payout
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs opacity-90">{s.detail}</p>
      {payout.payout_batch_id && (
        <p className="text-xs font-medium">
          Sent in a <Link to={`/creator-payments/batches?batch=${payout.payout_batch_id}`} className="underline">weekly payout batch</Link>
          {payout.status === 'needs_reconciliation'
            ? ' — sync the batch there; it is resolved as a whole.'
            : ['failed', 'returned', 'cancelled'].includes(payout.status)
              ? ' — this hire can be paid again on its own.'
              : '.'}
        </p>
      )}
      {payout.error_message && (
        <p className="text-xs font-medium break-words">{payout.error_message}</p>
      )}
      {note && <p className="text-xs italic">{note}</p>}
      <p className="text-[11px] opacity-75 break-all">
        by {payout.initiated_by_email} · {format(new Date(when), 'MMM d, yyyy h:mm a')}
        {payout.trolley_payment_id && <> · payment {payout.trolley_payment_id}</>}
        {payout.trolley_batch_id && <> · batch {payout.trolley_batch_id}</>}
        {payout.trolley_status && <> · Trolley: {payout.trolley_status}</>}
      </p>
    </div>
  )
}
